import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CPageModule from '../../components/CPageModule';
import CPageModuleBrowsableTable from '../../components/CPageModuleBrowsableTable';
import TagButton from '../../components/TagButton';
import Page from '../../components/layout/Page';
import { ETableIds } from '../../enums/ETableIds';
import { ETonalButtonClassNames } from '../../enums/ETagButtonColors';
import { EUserRoles } from '../../enums/EUserRoles';
import { PageRoutes } from '../../enums/enums';
import HGeneric from '../../helpers/HGeneric';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import type { IAugmentedBackendResponse } from '../../models/IAugmentedBackendResponse';
import type { IRequestParams } from '../../models/IRequestParams';
import type { IResponse } from '../../models/IResponse';
import type { ISortingMeta } from '../../models/ISortingMeta';
import type { TPandasTable } from '../../models/TPandasTable';
import UserManagementService from '../../services/userManagementService';
import { updateUserObject } from '../../store/userSlice';

interface State {
  snapshot: IResponse<IAugmentedBackendResponse<TPandasTable>> & IRequestParams;
}

function parseRoles(value: unknown): EUserRoles[] {
  return String(value ?? '')
    .split(',')
    .filter((role): role is EUserRoles => Object.values(EUserRoles).includes(role as EUserRoles));
}

function getRoleLabel(role: string): string {
  return role.replace('nexdos-app-', '').split('-').join(' ');
}

function getRoleColor(role: string): ETonalButtonClassNames {
  switch (role) {
    case EUserRoles.ADMIN:
      return ETonalButtonClassNames.TEAL_COMP_5;
    case EUserRoles.STAFF:
      return ETonalButtonClassNames.TEAL_GREEN_1;
    case EUserRoles.PORTFOLIO_MANAGER:
      return ETonalButtonClassNames.TEAL_PPT_1;
    case EUserRoles.PROFESSIONAL_CLIENT:
      return ETonalButtonClassNames.TEAL_PPT_2;
    case EUserRoles.SUITABLE_COUNTERPARTY:
      return ETonalButtonClassNames.TEAL_PPT_3;
    default:
      return ETonalButtonClassNames.DEFAULT;
  }
}

function renderRoles(value: unknown) {
  const roles = String(value ?? '').split(',').filter(Boolean);

  if (roles.length === 0) return null;

  return (
    <div className={'flex items-center justify-center gap-2 w-full'}>
      {roles.map((role) => (
        <TagButton key={role} color={getRoleColor(role)}>
          {getRoleLabel(role)}
        </TagButton>
      ))}
    </div>
  );
}

export default function UsersOverview() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { locale } = useAppSelector((state) => state.dashboard);
  const { keycloak } = useKeycloak();
  const keycloakUserId = keycloak.tokenParsed?.sub;

  const defaultSorting: ISortingMeta = {
    sortCol: 0,
    ascending: 'descending'
  };

  const [state, setState, getState] = useStateExtended<State>({
    snapshot: {
      state: {
        isLoading: true,
        isError: false
      },
      params: {
        sorting: defaultSorting
      }
    }
  });

  const fetchUsers = async (page: number, sorting?: ISortingMeta, search?: string) => {
    const effectiveSorting = sorting ?? defaultSorting;

    await updateStateResponseStatefulAttribute<State, typeof state.snapshot.data>(
      {
        snapshot: (await getState()).snapshot
      },
      setState,
      async () =>
        await UserManagementService.getKeycloakUsers({
          page,
          sorting: effectiveSorting,
          search
        })
    );

    updateStateAttribute<State>(
      {
        snapshot: {
          ...(await getState()).snapshot,
          params: {
            sorting: effectiveSorting,
            search
          }
        }
      },
      setState
    );
  };

  useEffect(() => {
    const sorting: ISortingMeta = {
      sortCol: Number.parseInt(searchParams.get('sortCol') ?? defaultSorting.sortCol.toString()),
      ascending: (searchParams.get('ascending') as ISortingMeta['ascending']) ?? defaultSorting.ascending
    };

    const page = Number.parseInt(searchParams.get('page') ?? '1');
    const search = searchParams.get('search') ?? undefined;

    void fetchUsers(page, sorting, search);
  }, [locale]);

  const userOverviewTitle = CLocalizedTextStringStatic('userManagement.overview.title');

  const currentSorting = state.snapshot.params?.sorting ?? defaultSorting;
  return (
    <Page>
      <CPageModule>
        <h1>{userOverviewTitle}</h1>
      </CPageModule>

      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        {state.snapshot.data?.meta && (
          <CPageModuleBrowsableTable
            id={ETableIds.USER_MANAGEMENT}
            data={state.snapshot.data.results}
            state={state.snapshot.state}
            tableMeta={{
              ...state.snapshot.data.meta,
              sorting: [
                {
                  columnIndex: Number(currentSorting.sortCol),
                  direction: currentSorting.ascending === 'ascending' ? 'asc' : 'desc'
                }
              ]
            }}
            onUpdateTableMeta={async (nextMeta) => {
              const page = nextMeta.page ?? 1;
              const sorting = nextMeta.sorting ?? defaultSorting;
              const search = nextMeta.search ?? '';

              await fetchUsers(page, sorting, search);

              searchParams.set('page', page.toString());
              searchParams.set('sortCol', sorting.sortCol.toString());
              searchParams.set('ascending', sorting.ascending);
              searchParams.set('search', search);

              setSearchParams(searchParams, { replace: true });
            }}
            columnConfig={[
              {
                span: 2,
                header: {
                  style: () => ({ textAlign: 'left' })
                },
                body: {
                  style: () => ({ textAlign: 'left' })
                }
              },
              {
                span: 2,
                header: {
                  style: () => ({ textAlign: 'left' })
                },
                body: {
                  style: () => ({ textAlign: 'left' })
                }
              },
              {
                span: 3,
                header: {
                  style: () => ({ textAlign: 'left' })
                },
                body: {
                  style: () => ({ textAlign: 'left' })
                }
              },
              {
                span: 2,
                body: {
                  style: () => ({ textAlign: 'center' }),
                  render: (context) =>
                    HGeneric.formatString(context.value as number | undefined, {
                      in: 'tzTimestamp',
                      out: 'localeDateString'
                    })
                }
              },
              {
                span: 0
              },
              {
                span: 2,
                isSortable: false,
                body: {
                  style: () => ({ textAlign: 'center' }),
                  render: (context) => renderRoles(context.value)
                }
              }
            ]}
            rowConfig={{
              body: {
                onClick: (context) => {
                  const [firstName, lastName, email, , id, rolesValue] = context.rowValues.slice(1, -1);
                  const roles = parseRoles(rolesValue);

                  dispatch(
                    updateUserObject({
                      firstName,
                      lastName,
                      email,
                      id,
                      isCurrentUser: id === keycloakUserId,
                      isAdmin: roles.includes(EUserRoles.ADMIN),
                      isStaff: roles.includes(EUserRoles.STAFF),
                      isPortfolioManager: roles.includes(EUserRoles.PORTFOLIO_MANAGER),
                      roles
                    })
                  );

                  navigate(PageRoutes.USERS_USER_HUB_OUTLET);
                }
              }
            }}
          />
        )}
      </CPageModule>
    </Page>
  );
}
